import React, { useState } from "react";
import { Modal, RadioGroup } from "@truevault/aquilo";
import { SurveyAnswers } from "../../../../common/hooks/api/useSurvey";
import { SHOPIFY_SNI_SLUG } from "../../../surveys/sellingSurvey";
import { BadgedTextPill } from "../vendor-documentation/VendorDocumentationPage";

interface ShopifySNIQuestionProps {
  radioGroupNamePrefix: string;
  sellingAndSharingAnswers: SurveyAnswers;
  setIsDisabledWhileLoading: (value: boolean) => void;
  setShopifySNIAnswer: (newAnswer: boolean) => Promise<void>;
  /** Where the "Incomplete" pill renders while the question is unanswered. */
  incompleteBadgePlacement: "below-question" | "inline-question";
  className?: string;
}

export function hasShopifySNIAnswer(sellingAndSharingAnswers: SurveyAnswers): boolean {
  const answer = sellingAndSharingAnswers[SHOPIFY_SNI_SLUG];
  return answer === "true" || answer === "false";
}

export function ShopifySNIQuestion({
  radioGroupNamePrefix,
  sellingAndSharingAnswers,
  setIsDisabledWhileLoading,
  setShopifySNIAnswer,
  incompleteBadgePlacement,
  className,
}: ShopifySNIQuestionProps) {
  const [pendingAnswer, setPendingAnswer] = useState<"YES" | "NO" | null>(null);
  const storedAnswer = sellingAndSharingAnswers[SHOPIFY_SNI_SLUG];
  const selectedValue =
    storedAnswer === "true" ? "YES" : storedAnswer === "false" ? "NO" : undefined;
  const showIncompleteBadge = selectedValue === undefined;
  const incompleteBadgeSpacingClass = {
    "inline-question": "inline-block ml-[8px]",
    "below-question": "mt-[8px]",
  }[incompleteBadgePlacement];

  return (
    <div className={className}>
      <div className="flex justify-between items-start gap-[12px]">
        <div className="font-medium text-gray-700">
          Has your business turned off Shopify Network Intelligence (SNI)?
          {showIncompleteBadge && (
            <div className={incompleteBadgeSpacingClass}>
              <BadgedTextPill cellPillDisplay={{ color: "yellow", text: "Incomplete" }} />
            </div>
          )}
        </div>
        <RadioGroup
          name={`${radioGroupNamePrefix}-shopifyNetworkIntelligence`}
          direction="horizontal"
          options={[
            { label: "Yes", value: "YES" },
            { label: "No", value: "NO" },
          ]}
          selectedValue={selectedValue}
          onChange={async (newValue: string) => {
            if (newValue === selectedValue) {
              return;
            }

            if (selectedValue === undefined) {
              setIsDisabledWhileLoading(true);
              await setShopifySNIAnswer(newValue === "YES");
              return;
            }

            setPendingAnswer(newValue as "YES" | "NO");
          }}
        />
      </div>

      {pendingAnswer !== null && (
        <ShopifySNIConfirmationModal
          newAnswer={pendingAnswer}
          onConfirm={async () => {
            setIsDisabledWhileLoading(true);
            setPendingAnswer(null);
            await setShopifySNIAnswer(pendingAnswer === "YES");
          }}
          onCancel={() => setPendingAnswer(null)}
        />
      )}
    </div>
  );
}

function ShopifySNIConfirmationModal({
  newAnswer,
  onConfirm,
  onCancel,
}: {
  newAnswer: "YES" | "NO";
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) {
  const isAdding = newAnswer === "NO";
  return (
    <Modal
      title={
        isAdding
          ? "Turning on Shopify Network Intelligence (SNI)"
          : "Turning off Shopify Network Intelligence (SNI)"
      }
      body={
        isAdding
          ? "Turning on SNI will add Shopify Network Intelligence to your vendor list."
          : "Turning off SNI will remove Shopify Network Intelligence from your vendor list."
      }
      alignContent="left"
      alignText="left"
      alignButton="right"
      secondButton="Confirm"
      secondButtonColor="primary"
      onSecondButtonClick={onConfirm}
      firstButton="Cancel"
      onFirstButtonClick={onCancel}
      showOverlay
      onClose={onCancel}
    />
  );
}
